import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import classNames from "classnames";
import { Table } from "react-bootstrap";

import { useUmpiresContext } from "../../../contexts/Umpires";
import { ColHeaders, SortColumns } from "../types/umpires";

import { usePaginationContext } from "../../../../../../contexts/Pagination";
import { setSortingClassNames } from "../lib/setSortingClassNames";
import Loading from "../../../../../../components/Loading";

const UmpiresTable = React.memo(() => {
    const { umpires, sortColumn, setSortColumn, setSortOrder } =
        useUmpiresContext();
    const { page, itemsPerPage: usersPerPage } = usePaginationContext();

    const paginatedUsers = useMemo(() => {
        return umpires.slice(
            page * usersPerPage - usersPerPage,
            page * usersPerPage,
        );
    }, [umpires, page, usersPerPage]);

    const handleHeaderClick = (column: SortColumns) => {
        if (column !== sortColumn) {
            setSortColumn(column);
            setSortOrder("ascending");
        } else {
            setSortOrder(prev =>
                prev === "ascending" ? "descending" : "ascending",
            );
        }
    };

    const colHeaders: ColHeaders[] = [
        {
            displayName: "First Name",
            sortable: "firstName",
        },
        {
            displayName: "Last Name",
            sortable: "lastName",
        },
        {
            displayName: "Email",
            sortable: "email",
        },
        {
            displayName: "City",
            sortable: "city",
        },
        {
            displayName: "State",
            sortable: "state",
        },
        {
            displayName: "Phone",
            sortable: null,
        },
        {
            displayName: "Games",
            sortable: "games",
        },
        {
            displayName: "Ratings",
            sortable: "ratings",
        },
        {
            displayName: "Action",
            sortable: null,
        },
    ];

    return (
        <Table
            striped
            className={classNames({
                dataTable: true,
            })}
        >
            <thead>
                <tr>
                    {colHeaders.map((c, idx) => (
                        <th
                            key={`umpiresTable-thead-${idx}`}
                            onClick={() =>
                                c.sortable ? handleHeaderClick(c.sortable) : {}
                            }
                            className={
                                c.sortable
                                    ? classNames(
                                          setSortingClassNames(c.sortable),
                                      )
                                    : ""
                            }
                            data-sortable={c.sortable ? "true" : "false"}
                        >
                            {c.displayName}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {umpires.length ? (
                    paginatedUsers.map(u => {
                        return (
                            <tr key={u.id}>
                                <td style={{ width: "12%" }}>
                                    {u.meta.firstName}
                                </td>
                                <td style={{ width: "12%" }}>
                                    {u.meta.lastName}
                                </td>
                                <td style={{ width: "25%" }}>
                                    <a href={`mailto:${u.email}`}>{u.email}</a>
                                </td>
                                <td style={{ width: "15%" }}>{u.meta.city}</td>
                                <td style={{ width: "7%" }}>{u.meta.state}</td>
                                <td style={{ width: "10%" }}>
                                    {u.meta.phoneNumbers.primary}
                                </td>
                                <td style={{ width: "7%" }}>{u.games}</td>
                                <td style={{ width: "7%" }}>{u.ratings}</td>
                                <td style={{ width: "5%" }}>
                                    <Link to={`/admin/umpires/${u.id}`}>
                                        View
                                    </Link>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={9}>
                            <Loading />
                        </td>
                    </tr>
                )}
            </tbody>
            <tfoot>
                <tr>
                    {colHeaders.map((c, idx) => (
                        <th key={`umpiresTable-tfoot-${idx}`}>
                            {c.displayName}
                        </th>
                    ))}
                </tr>
            </tfoot>
        </Table>
    );
});

export default UmpiresTable;
